import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { Box, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { DATE_FORMAT, useLocalization } from '@ui-framework/index';
import { IconLibrary } from '@product-framework/index';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import { TIME_SLOT_KEYS, TIME_SLOT_LABELS } from './availability';

function makeRow() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    date: null,
    slotKeys: ['allDay'],
  };
}

function DispatchDateSlotPicker({
  rows,
  onAddRow,
  onRemoveRow,
  onDateChange,
  onToggleSlot,
}) {
  const { formatMessage } = useLocalization();

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {formatMessage({
            id: 'DISPATCH_ADD_DATE_HINT',
            defaultMessage:
              'Add one or more dates and choose time slots for each. You can remove a row if it is not needed.',
          })}
        </Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={onAddRow}>
          {formatMessage({
            id: 'DISPATCH_ADD_DATE',
            defaultMessage: 'Add date',
          })}
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {rows.map(row => (
        <Box
          key={row.id}
          sx={{
            p: 0.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
          }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-start"
            sx={{
              flexWrap: 'nowrap',
              overflowX: 'auto',
              minHeight: 40,
            }}>
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: 160, sm: 180 },
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'center',
              }}>
              <DatePicker
                value={row.date}
                onChange={v => onDateChange(row.id, v)}
                minDate={dayjs().startOf('day')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      my: 1,
                      '& .MuiInputBase-root': { alignItems: 'center' },
                    },
                  },
                }}
              />
            </Box>
            <FormGroup
              row
              aria-label={formatMessage({
                id: 'DISPATCH_TIME_SLOTS',
                defaultMessage: 'Time slots',
              })}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flex: '1 1 auto',
                flexWrap: 'nowrap',
                gap: 0.25,
                alignItems: 'center',
                alignSelf: 'center',
                minWidth: 0,
              }}>
              {TIME_SLOT_KEYS.map(slotKey => (
                <FormControlLabel
                  key={`${row.id}-${slotKey}`}
                  control={
                    <Checkbox
                      size="small"
                      checked={row.slotKeys.includes(slotKey)}
                      onChange={() => onToggleSlot(row.id, slotKey)}
                      sx={{ py: 0 }}
                    />
                  }
                  label={TIME_SLOT_LABELS[slotKey]}
                  sx={{
                    mr: 0,
                    ml: 0,
                    my: 0,
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.8125rem',
                      lineHeight: 1.25,
                    },
                  }}
                />
              ))}
            </FormGroup>
            <IconButton
              aria-label={formatMessage({
                id: 'DISPATCH_REMOVE_DATE_ROW',
                defaultMessage: 'Remove this date',
              })}
              color="error"
              disabled={rows.length <= 1}
              title={
                rows.length <= 1
                  ? formatMessage({
                      id: 'DISPATCH_REMOVE_DATE_ROW_DISABLED',
                      defaultMessage:
                        'At least one date row is required. Add another row before removing this one.',
                    })
                  : undefined
              }
              onClick={() => onRemoveRow(row.id)}
              sx={{
                flexShrink: 0,
                alignSelf: 'center',
                my: 0,
              }}>
              <DeleteOutline />
            </IconButton>
          </Stack>
        </Box>
      ))}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={onAddRow}
        sx={{ alignSelf: 'flex-start' }}>
        {formatMessage({
          id: 'DISPATCH_ADD_ANOTHER_DATE',
          defaultMessage: 'Add another date',
        })}
      </Button>
    </Stack>
  );
}

function DispatchCalendarModal({ open, onClose, selectedSlot, jobInfo }) {
  const { formatMessage } = useLocalization();
  const { serviceJobNumber } = jobInfo;

  const [dateSlotRows, setDateSlotRows] = useState(() => [makeRow()]);

  useEffect(() => {
    if (open && !selectedSlot?.slotKey) {
      setDateSlotRows([makeRow()]);
    }
  }, [open, selectedSlot?.slotKey]);

  const addRow = useCallback(() => {
    setDateSlotRows(prev => [...prev, makeRow()]);
  }, []);

  const removeRow = useCallback(id => {
    setDateSlotRows(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(r => r.id !== id);
    });
  }, []);

  const setRowDate = useCallback((id, value) => {
    setDateSlotRows(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, date: value ? dayjs(value).startOf('day') : null }
          : r,
      ),
    );
  }, []);

  const toggleRowSlot = useCallback((id, slotKey) => {
    setDateSlotRows(prev =>
      prev.map(r => {
        if (r.id !== id) return r;
        const has = r.slotKeys.includes(slotKey);
        if (has) {
          const next = r.slotKeys.filter(k => k !== slotKey);
          return {
            ...r,
            slotKeys: next.length === 0 ? ['allDay'] : next,
          };
        }
        if (slotKey === 'allDay') {
          return { ...r, slotKeys: ['allDay'] };
        }
        const withoutAllDay = r.slotKeys.filter(k => k !== 'allDay');
        return { ...r, slotKeys: [...withoutAllDay, slotKey] };
      }),
    );
  }, []);

  const buttons = [
    {
      children: formatMessage({ id: 'DISPATCH', defaultMessage: 'Dispatch' }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      onClick: () => {
        window.location.href = `/servicejobs/edit/${jobInfo?.id}`;
        onClose(null);
      },
      startIcon: <IconLibrary.Dispatch />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        onClose(null);
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const selectedSlotDateParsed = dayjs(selectedSlot?.dateStr);
  const selectedSlotDateLabel = selectedSlotDateParsed.isValid()
    ? selectedSlotDateParsed.format(DATE_FORMAT.FRIENDLY_WEEKDAY_DATE)
    : '';

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth={selectedSlot?.slotKey ? 'sm' : 'md'}
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.AcceptServiceJob />
        {formatMessage({
          id: 'DISPATCH_JOB',
          defaultMessage: 'Dispatch Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>

      <DialogContent>
        {selectedSlot?.slotKey ? (
          <Box>
            <Typography variant="body">
              Do you want to dispatch the job for{' '}
              <Typography component="span" sx={{ fontWeight: 500 }}>
                {selectedSlotDateLabel} &ndash;{' '}
                {TIME_SLOT_LABELS[selectedSlot.slotKey] ??
                  selectedSlot.slotKey.charAt(0).toUpperCase() +
                    selectedSlot.slotKey.slice(1)}
              </Typography>{' '}
              time slot?
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <DispatchDateSlotPicker
              rows={dateSlotRows}
              onAddRow={addRow}
              onRemoveRow={removeRow}
              onDateChange={setRowDate}
              onToggleSlot={toggleRowSlot}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        {buttons.map(btn => {
          const { children, ...rest } = btn;
          return (
            <Button key={children} {...rest}>
              {children}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

export default DispatchCalendarModal;
